import { useTranslation } from "react-i18next";
import { CheckIcon, CloseIcon } from "./icons";
import type { DerivedJobStatus } from "../utils/jobStatus";

interface Props {
  steps: readonly string[];
  stepLabelKeys: Record<string, string>;
  currentStepIndex: number;
  jobStatus: DerivedJobStatus;
}

/**
 * Horizontal stepper with numbered circles, connecting lines, and
 * active/completed/failed visual states. Shared by install and media-push modals.
 */
const StepperProgress = ({ steps, stepLabelKeys, currentStepIndex, jobStatus }: Props) => {
  const { t } = useTranslation();
  const { isCancelled, isSuccess, isError, isDone } = jobStatus;

  return (
    <div className="relative flex items-start mb-5 shrink-0">
      {steps.map((step, i) => {
        const isActive = i === currentStepIndex && !isDone;
        const isCompleted = isSuccess ? true : !isCancelled && !isError && i < currentStepIndex;
        const isFailed = (isCancelled || isError) && i === currentStepIndex;
        return (
          <div key={step} className="flex flex-col items-center flex-1 relative z-10">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                isCompleted
                  ? "text-white bg-green-500"
                  : isActive
                  ? "text-white bg-green-500"
                  : isFailed
                  ? "text-white bg-gray-400"
                  : "bg-gray-100 text-gray-400"
              }`}
              style={isActive ? { boxShadow: "0 0 0 4px rgba(0,200,137,0.15)" } : undefined}
            >
              {isCompleted ? (
                <CheckIcon className="w-4 h-4" />
              ) : isFailed ? (
                <CloseIcon className="w-4 h-4" />
              ) : (
                i + 1
              )}
            </div>
            <span
              className={`text-[11px] mt-1.5 text-center ${
                isActive || isCompleted
                  ? "font-medium text-green-500"
                  : isFailed
                  ? "font-medium text-gray-500"
                  : "text-gray-400"
              }`}
            >
              {t(stepLabelKeys[step])}
            </span>
          </div>
        );
      })}

      {/* Connecting lines between steps */}
      {steps.length > 1 &&
        Array.from({ length: steps.length - 1 }, (_, i) => {
          const pct = 100 / steps.length;
          const halfStep = pct / 2;
          const left = `${pct * i + halfStep}%`;
          const right = `${100 - pct * (i + 1) - halfStep + pct}%`;
          const isLineActive = currentStepIndex > i && !isCancelled && !isError;
          return (
            <div
              key={`line-${i}`}
              className={`absolute h-0.5 rounded-full transition-colors duration-300 ${
                isLineActive || isSuccess ? "bg-green-500" : "bg-gray-200"
              }`}
              style={{ top: 16, left, right }}
            />
          );
        })}
    </div>
  );
};

export default StepperProgress;
